/**
 * The coffee machine (zustand): the connected Manetta driver, its info and last Status, Turn on / Turn off, the shot
 * the guided brew starts, follows and stops (shot.ts), and steam mode for a milk drink (steam.ts). Like the scale
 * store it reconnects after a drop and, in Chrome, connects on its own at start to the machine connected last
 * (`getDevices()`).
 *
 * The driver (manetta.ts, with @taretto/manetta and its protobuf runtime) loads on the first connect, so the app
 * costs nothing more for people without a machine. The picker opens before that, inside the click.
 *
 * Ownership, as in the scale store: every user action first retires whatever is in flight and bumps `generation`;
 * async continuations check it after every await and back off when they were superseded.
 */
import { create } from 'zustand';
import { thisBrowser, track } from '@/lib/usage';
import { requestMachineDevice } from './bluetooth';
import {
  PING_MS,
  SHOT_MAX_MS,
  STATUS_QUIET_MS,
  STOP_CONFIRM_MS,
  STOP_MAX_SENDS,
  STOP_REPEAT_MS,
  shotOver,
  shotRunning,
  startProblem,
  type MachineShot,
} from './shot';
import {
  STEAM_ATTEMPTS,
  STEAM_DIDNT_START,
  STEAM_FOLLOW_MS,
  STEAM_MAX_MS,
  STEAM_STILL_ON,
  inSteam,
  steamProblem,
  steamReadyBucket,
  type SteamEnterVia,
  type SteamLeaveVia,
  type SteamSession,
} from './steam';
import {
  MachinePairingError,
  MachineTooNewError,
  type MachineCommand,
  type MachineCommandResult,
  type MachineDriver,
  type MachineInfo,
  type MachineState,
} from './types';
import type { FakeManettaDevice } from './fakeDevice';

export type MachineConnection = 'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'error';
type ConnectVia = 'picker' | 'reconnect' | 'auto';
/** What a power command was for, for usage data: the Scale page's or the brew step's button, setup's or Home's machine line, or after saving. */
export type PowerVia = 'button' | 'setup' | 'home' | 'after_save';
/**
 * Why Taretto stops the shot: the cut-off cue's moment, the target (stopAtTargetYield), Stop tapped, or leaving the
 * brew step mid-shot (Exit, Next, Back, Restart, the cup lifted). The guided brew's stopPump says which.
 */
export type ShotStopVia = 'cutoff' | 'target' | 'stop' | 'leave';
type CommandVia = PowerVia | 'cutoff' | 'target' | 'leave' | SteamEnterVia | SteamLeaveVia;

/** The machine connected last, by Web Bluetooth device id, for auto-connect. Disconnect forgets it. */
const REMEMBERED_KEY = 'taretto.machineDevice';
/** Quick reconnect attempts after a drop (1, 2, 4, 8, 10 s apart), shown as 'reconnecting'. */
export const MAX_RECONNECT_ATTEMPTS = 5;
export const AUTO_CONNECT_TIMEOUT_MS = 5_000;
/** After the quick attempts, keep retrying quietly while the page is visible… */
export const BACKGROUND_RETRY_MS = 30_000;
/** …for this long after the connection was lost. */
export const BACKGROUND_RETRY_FOR_MS = 10 * 60_000;
const BACKGROUND_CONNECT_TIMEOUT_MS = 5_000;
/**
 * After the device took a power command, how long Status gets to show the machine there before the command goes again.
 * Status can take a few seconds to show it.
 */
export const POWER_FOLLOW_MS = 6_000;
/** Sends of one power command before Taretto says to use the machine's own button. */
export const POWER_ATTEMPTS = 3;

export const LOST_MACHINE = 'Lost the connection to the machine. Make sure it’s plugged in, then tap Reconnect.';
export const LOST_MACHINE_RETRYING = 'Lost the connection to the machine. Still trying in the background, or tap Reconnect.';
export const MACHINE_DIDNT_TURN_ON = 'The machine didn’t turn on. Try its Power button.';
export const MACHINE_DIDNT_TURN_OFF = 'The machine didn’t turn off. Try its Power button.';
export const FORGET_MACHINE_HINT = 'If it keeps failing, forget the machine in this device’s Bluetooth settings, then pair again.';

/** Why a command the device answered didn't happen, in words for someone at the machine. */
export function commandProblem(command: MachineCommand, result: MachineCommandResult): string | null {
  switch (result) {
    case 'ok':
      return null;
    case 'busy':
      return command === 'turnOff' ? 'The machine is brewing. Stop the shot first.' : 'The machine is busy. Try again in a moment.';
    case 'not_ready':
      return 'The board can’t tell whether the machine is on right now. Try again in a moment.';
    case 'fault':
      return 'The machine reports a fault. Check it, then try again.';
    case 'unsupported':
      return 'This machine can’t do that.';
    case 'invalid':
      return 'The machine didn’t take the command. Its firmware may not match this version of Taretto.';
    case 'no_reply':
      return 'The machine didn’t answer. Make sure it’s plugged in and nearby.';
  }
}

/** The kind of a failed connect, for usage data (machine_connect_failed), sorted from the error the card shows. */
export function machineConnectFailure(e: unknown): 'bluetooth-off' | 'blocked' | 'unsupported' | 'not-a-machine' | 'too-new' | 'unreachable' | 'timeout' | 'other' {
  if (e instanceof MachineTooNewError) return 'too-new';
  const msg = e instanceof Error ? e.message : String(e);
  if (/^Timed out/.test(msg)) return 'timeout';
  if (/turned off or unavailable/.test(msg)) return 'bluetooth-off';
  if (/blocked|needs HTTPS/i.test(msg)) return 'blocked';
  if (/Web Bluetooth isn’t available|requestDevice is not a function|undefined/.test(msg)) return 'unsupported';
  if (/isn’t a machine with a Manetta board/.test(msg)) return 'not-a-machine';
  if (/Couldn’t connect to the machine/.test(msg)) return 'unreachable';
  return 'other';
}

export interface MachineStoreState {
  status: MachineConnection;
  /** The machine's name while connecting or connected. */
  deviceName: string | null;
  info: MachineInfo | null;
  /** The last Status. Null while not connected: nothing stale is shown. */
  state: MachineState | null;
  /** Why connecting failed or the link was lost. */
  error: string | null;
  /** Why the last command didn't do what it should, until the next one or dismissNotice(). */
  notice: string | null;
  /** The power command on its way, until the machine got there or Taretto gave up. */
  pending: MachineCommand | null;
  /** The first command on this connection is pairing: the OS may ask to accept it. */
  pairing: boolean;
  /** A machine from earlier in this session, which reconnect() reaches without the picker. */
  knownMachine: string | null;
  /** The dev build's virtual machine. */
  virtual: boolean;
  /** Opens the picker (call it inside the click), or the virtual machine in dev builds. */
  connect(opts?: { virtual?: boolean }): Promise<void>;
  reconnect(): Promise<void>;
  /** Disconnects and forgets the machine for auto-connect, or cancels a connect in progress. */
  disconnect(): Promise<void>;
  /** At start: the machine connected last, if the browser still permits it (Chrome's getDevices()). Quiet. */
  tryAutoConnect(): Promise<void>;
  /** Turn on or Turn off. Resolves true once Status shows the machine there (or it went off with the machine). */
  setPower(on: boolean, via?: PowerVia): Promise<boolean>;
  dismissNotice(): void;
  /** The shot the guided brew follows on the machine, until the next brew forgets it. */
  shot: MachineShot | null;
  /** start_brew is on its way. */
  starting: boolean;
  /**
   * Start brew: start_brew (capped at SHOT_MAX_MS), then a ping every PING_MS while it runs. Resolves true on the
   * device's OK, which is when the guided brew starts its timer; otherwise `notice` says why.
   */
  startShot(): Promise<boolean>;
  /** Follow the shot the machine shows brewing (started on the machine): the guided brew times it. Null without one. */
  followShot(): MachineShot | null;
  /**
   * Stop the shot: stop_brew, again every STOP_REPEAT_MS until Status shows it stopped, and `stopLate` after
   * STOP_CONFIRM_MS (SPEC 5.5). A brew the machine shows that Taretto didn't follow is stopped too.
   */
  stopShot(via?: ShotStopVia): void;
  /** A new brew: the last shot is no longer the guided brew's (one still running is stopped first). */
  forgetShot(): void;
  /** Steam mode Taretto switched on, until Status shows it over (and then as it ended, until the next). */
  steam: SteamSession | null;
  /** Why switching to steam or back didn't happen, until the next steam command or dismissNotice(). */
  steamNotice: string | null;
  /**
   * Switch to steam for a milk drink: enter_steam_mode (capped at STEAM_MAX_MS) until Status shows steam mode.
   * Resolves true once it does. Nothing when the machine is in steam mode already: that steam is the person's.
   */
  enterSteam(via: SteamEnterVia): Promise<boolean>;
  /**
   * Back to brew heat, only from steam Taretto switched on: leave_steam_mode until Status shows brew heat, else
   * `steamNotice` says to switch it off on the machine (steam-mode.md).
   */
  leaveSteam(via: SteamLeaveVia): void;
}

let driver: MachineDriver | null = null;
/** The driver last connected, kept across a disconnect for reconnect(). */
let known: MachineDriver | null = null;
let virtualDevice: FakeManettaDevice | null = null;
let generation = 0;
let unsubs: Array<() => void> = [];
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempt = 0;
let connectionLostAt = 0;
let pairingFailures = 0;
/** Set while a turn_off waits for a device that goes off with the machine to disconnect. */
let offWithMachine: (() => void) | null = null;
let shotSeq = 0;
/** The heartbeat while a shot Taretto started runs, and the check for Status going quiet. */
let pingTimer: ReturnType<typeof setInterval> | null = null;
let pingInFlight = false;
let quietTimer: ReturnType<typeof setInterval> | null = null;
let unwatchPage: (() => void) | null = null;
/** performance.now() of the last Status. */
let lastStatusAt = 0;
/** The shot a stop_brew loop is running for. */
let stoppingId: number | null = null;
let steamSeq = 0;
/** The steam session a leave_steam_mode loop is running for. */
let leavingId: number | null = null;
/** A switch back asked for while enter_steam_mode was on its way: it goes once the device has taken it. */
let leaveAfterEnter: { id: number; via: SteamLeaveVia } | null = null;

/** The switch back asked for while session `id`'s enter_steam_mode was on its way, if one was; forgets it. */
function takeLeaveAfterEnter(id: number): SteamLeaveVia | null {
  const pending = leaveAfterEnter;
  if (pending?.id !== id) return null;
  leaveAfterEnter = null;
  return pending.via;
}

const loadDriver = () => import('./manetta');

function isCancel(e: unknown): boolean {
  return e instanceof Error && /cancel/i.test(e.message);
}

function pageVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

function readRemembered(): string | null {
  try {
    return localStorage.getItem(REMEMBERED_KEY);
  } catch {
    return null;
  }
}

function writeRemembered(id: string | null): void {
  try {
    if (id) localStorage.setItem(REMEMBERED_KEY, id);
    else localStorage.removeItem(REMEMBERED_KEY);
  } catch {
    // Storage blocked (private mode): auto-connect just won't happen.
  }
}

function powerField(info: MachineInfo): 'none' | 'state' | 'off' | 'on' {
  const p = info.capabilities.power;
  return !p ? 'none' : p.turnOn ? 'on' : p.turnOff ? 'off' : 'state';
}

/** The dev build's virtual machine, for its panel: press its buttons, drop the link. */
export function getVirtualMachine(): FakeManettaDevice | null {
  return import.meta.env.DEV ? virtualDevice : null;
}

/** The connected driver, for the guided brew. */
export function getMachine(): MachineDriver | null {
  return driver?.connected ? driver : null;
}

/** Resolves true once Status satisfies `pred`, false after `ms` or when the link drops. */
function watchFor(d: MachineDriver, pred: (s: MachineState) => boolean, ms: number): Promise<boolean> {
  if (d.state && pred(d.state)) return Promise.resolve(true);
  return new Promise((resolve) => {
    const done = (ok: boolean) => {
      clearTimeout(timer);
      offState();
      offDrop();
      resolve(ok);
    };
    const timer = setTimeout(() => done(false), ms);
    const offState = d.onState((s) => pred(s) && done(true));
    const offDrop = d.onDisconnect(() => done(false));
  });
}

const DISCONNECTED = { info: null, state: null, pending: null, pairing: false } as const;

export const useMachine = create<MachineStoreState>()((set, get) => {
  function detach() {
    unsubs.forEach((fn) => fn());
    unsubs = [];
  }

  /** Stops whatever is in flight: a reconnect loop, a connect. The caller sets the new status. */
  function retire() {
    if (driver?.connected) shotLinkLost();
    generation++;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = null;
    reconnectAttempt = 0;
    offWithMachine = null;
    detach();
    const d = driver;
    driver = null;
    if (d) void d.disconnect().catch(() => {});
  }

  function connectedState(d: MachineDriver) {
    return { status: 'connected' as const, deviceName: d.name, info: d.info, state: d.state, error: null, pairing: false };
  }

  function attach(d: MachineDriver) {
    detach();
    driver = d;
    unsubs.push(
      d.onState((state) => {
        if (driver !== d) return;
        set({ state });
        shotStatus(d, state);
        steamStatus(d, state);
      }),
    );
    unsubs.push(d.onDisconnect(() => handleDrop(d)));
  }

  function remember(d: MachineDriver, virtual: boolean) {
    known = d;
    pairingFailures = 0;
    set({ knownMachine: d.name });
    if (!virtual) writeRemembered(d.deviceId);
  }

  function trackConnected(d: MachineDriver, via: ConnectVia) {
    if (get().virtual || !d.info) return;
    const caps = d.info.capabilities;
    track('machine_connected', { ...thisBrowser(), via, brew: caps.brew, power: powerField(d.info), steam: caps.steam });
  }

  function trackFailed(via: ConnectVia, e: unknown) {
    if (!get().virtual) track('machine_connect_failed', { ...thisBrowser(), via, reason: machineConnectFailure(e) });
  }

  function handleDrop(d: MachineDriver) {
    if (driver !== d) return;
    if (offWithMachine) {
      // A device that goes off with the machine disconnects after Turn off: that's the machine being off.
      offWithMachine();
      generation++;
      detach();
      driver = null;
      set({ status: 'disconnected', error: null, ...DISCONNECTED });
      return;
    }
    if (!get().virtual) track('machine_dropped', thisBrowser());
    shotLinkLost();
    connectionLostAt = performance.now();
    reconnectAttempt = 0;
    scheduleReconnect(d, ++generation);
  }

  function scheduleReconnect(d: MachineDriver, gen: number) {
    const background = reconnectAttempt >= MAX_RECONNECT_ATTEMPTS;
    if (background && performance.now() - connectionLostAt >= BACKGROUND_RETRY_FOR_MS) {
      retire();
      set({ status: 'error', error: LOST_MACHINE, ...DISCONNECTED });
      return;
    }
    const delay = background ? BACKGROUND_RETRY_MS : Math.min(1000 * 2 ** reconnectAttempt, 10_000);
    reconnectAttempt++;
    set(background ? { status: 'error', error: LOST_MACHINE_RETRYING, ...DISCONNECTED } : { status: 'reconnecting', error: null, ...DISCONNECTED });
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(() => void attemptReconnect(d, gen, background), delay);
  }

  async function attemptReconnect(d: MachineDriver, gen: number, background: boolean) {
    reconnectTimer = null;
    if (gen !== generation || driver !== d) return;
    if (background && !pageVisible()) {
      scheduleReconnect(d, gen);
      return;
    }
    try {
      await d.connect(background ? { timeoutMs: BACKGROUND_CONNECT_TIMEOUT_MS } : undefined);
    } catch {
      if (gen !== generation || driver !== d) return;
      scheduleReconnect(d, gen);
      return;
    }
    if (gen !== generation || driver !== d) return;
    reconnectAttempt = 0;
    set(connectedState(d));
  }

  /** Connects `d` and makes it the machine. `gen` is the action's generation. */
  async function open(d: MachineDriver, gen: number, via: ConnectVia, timeoutMs?: number): Promise<boolean> {
    set({ deviceName: d.name });
    await d.connect(timeoutMs ? { timeoutMs } : undefined);
    if (gen !== generation) {
      void d.disconnect().catch(() => {});
      return false;
    }
    attach(d);
    remember(d, get().virtual);
    trackConnected(d, via);
    set(connectedState(d));
    return true;
  }

  /** Resolves once the machine is where `on` asks, false after `ms` or when the link went. */
  function follows(d: MachineDriver, on: boolean, ms: number): Promise<boolean> {
    const there = (s: MachineState | null) => s?.power != null && (on ? s.power !== 'off' : s.power === 'off');
    if (there(d.state)) return Promise.resolve(true);
    return new Promise((resolve) => {
      const done = (ok: boolean) => {
        clearTimeout(timer);
        offState();
        offDrop();
        if (offWithMachine === wentOff) offWithMachine = null;
        resolve(ok);
      };
      const timer = setTimeout(() => done(false), ms);
      const offState = d.onState((s) => there(s) && done(true));
      const offDrop = d.onDisconnect(() => done(false));
      const wentOff = () => done(true);
      if (!on && d.info?.capabilities.power?.goesOffWithMachine) offWithMachine = wentOff;
    });
  }

  function report(command: 'turn_on' | 'turn_off' | 'start_brew' | 'stop_brew' | 'enter_steam_mode' | 'leave_steam_mode', result: MachineCommandResult | 'not_paired', followed: boolean, via: CommandVia) {
    if (!get().virtual) track('machine_command', { command, result, followed, via });
  }

  /** The device refused pairing (MachinePairingError): what to do, the second time with the forget hint. */
  function pairingNotice(e: MachinePairingError): string {
    pairingFailures++;
    return pairingFailures > 1 ? `${e.message} ${FORGET_MACHINE_HINT}` : e.message;
  }

  // ------------------------------------------------------------------ the shot

  function patchShot(id: number, patch: Partial<MachineShot>) {
    const shot = get().shot;
    if (shot?.id === id) set({ shot: { ...shot, ...patch } });
  }

  function endShotTimers() {
    if (pingTimer) clearInterval(pingTimer);
    if (quietTimer) clearInterval(quietTimer);
    pingTimer = quietTimer = null;
    unwatchPage?.();
    unwatchPage = null;
  }

  function beginShot(d: MachineDriver, startedBy: MachineShot['startedBy'], bootId: number, brewId: number, timed: boolean): MachineShot {
    endShotTimers();
    const shot: MachineShot = {
      id: ++shotSeq,
      at: Date.now(),
      startedBy,
      bootId,
      brewId,
      phase: 'running',
      stopAsked: false,
      stopLate: false,
      stopReason: null,
      timed,
      stopElapsedMs: null,
      restarted: false,
      linkLost: false,
      quiet: false,
      hidden: !pageVisible(),
    };
    set({ shot });
    lastStatusAt = performance.now();
    // A brew started with the machine's own controls doesn't depend on any connection (SPEC 5.4): no heartbeat.
    if (startedBy === 'taretto') pingTimer = setInterval(() => ping(d), PING_MS);
    quietTimer = setInterval(() => checkQuiet(shot.id), STATUS_QUIET_MS / 4);
    if (typeof document !== 'undefined') {
      const onVisibility = () => document.visibilityState === 'hidden' && patchShot(shot.id, { hidden: true });
      document.addEventListener('visibilitychange', onVisibility);
      unwatchPage = () => document.removeEventListener('visibilitychange', onVisibility);
    }
    return shot;
  }

  function ping(d: MachineDriver) {
    if (driver !== d || !d.connected || pingInFlight) return;
    pingInFlight = true;
    void d
      .send('ping')
      .catch(() => {})
      .finally(() => {
        pingInFlight = false;
      });
  }

  /** A brewing device that goes quiet for more than STATUS_QUIET_MS is in trouble (SPEC 3.4). */
  function checkQuiet(id: number) {
    const shot = get().shot;
    if (shot?.id !== id || shot.phase === 'stopped') return;
    const quiet = driver?.connected === true && driver.state?.phase === 'brewing' && performance.now() - lastStatusAt > STATUS_QUIET_MS;
    if (quiet !== shot.quiet) patchShot(id, { quiet });
  }

  /** Every Status: the shot is over (and why), still running, or a stop asked for before a reconnect goes on. */
  function shotStatus(d: MachineDriver, s: MachineState) {
    lastStatusAt = performance.now();
    const shot = get().shot;
    if (!shot || shot.phase === 'stopped') return;
    if (shotOver(shot, s)) {
      const restarted = s.bootId !== shot.bootId;
      const same = !restarted && s.brewId === shot.brewId;
      finishShot({ ...shot, restarted, stopReason: same ? s.stopReason : null, stopElapsedMs: same ? s.elapsedMs : null });
      return;
    }
    if (shot.quiet || shot.linkLost) patchShot(shot.id, { quiet: false, linkLost: false });
    if (shot.stopAsked && stoppingId !== shot.id && d.connected) void stopLoop(d, shot, null);
  }

  function finishShot(shot: MachineShot) {
    endShotTimers();
    const done: MachineShot = { ...shot, phase: 'stopped', quiet: false };
    set({ shot: done });
    if (!get().virtual) {
      const stopped = done.restarted ? 'restart' : (done.stopReason ?? 'unknown');
      track('machine_shot', { started: done.startedBy, stopped, asked: done.stopAsked, late: done.stopLate, background: done.hidden });
    }
  }

  /** The link went mid-shot: a shot Taretto started stops by itself (SPEC 5.4), so the heartbeat ends. */
  function shotLinkLost() {
    const shot = get().shot;
    if (!shotRunning(shot)) return;
    if (pingTimer) clearInterval(pingTimer);
    pingTimer = null;
    patchShot(shot!.id, { linkLost: true, quiet: false });
  }

  /** stop_brew until Status shows the shot over. `via` is reported once, for the stop Taretto was asked for. */
  async function stopLoop(d: MachineDriver, shot: MachineShot, via: ShotStopVia | null) {
    stoppingId = shot.id;
    const startedAt = performance.now();
    const late = setTimeout(() => patchShot(shot.id, { stopLate: true }), STOP_CONFIRM_MS);
    let first: MachineCommandResult | null = null;
    try {
      for (let i = 0; i < STOP_MAX_SENDS; i++) {
        // A drop says what happened; the next Status after a reconnect picks the stop up again.
        if (driver !== d || !d.connected) return;
        // Sent once whatever Status says: it may not show the shot yet, and a stop with nothing running does nothing.
        if (i > 0 && shotOver(shot, d.state)) return;
        let result: MachineCommandResult;
        try {
          result = await d.send('stopBrew');
        } catch {
          return;
        }
        first ??= result;
        if (await watchFor(d, (s) => shotOver(shot, s), STOP_REPEAT_MS)) return;
      }
    } finally {
      clearTimeout(late);
      if (stoppingId === shot.id) stoppingId = null;
      if (via && first) {
        const followed = shotOver(shot, d.state) && performance.now() - startedAt <= STOP_CONFIRM_MS;
        report('stop_brew', first, followed, via === 'stop' ? 'button' : via);
      }
    }
  }


  // ------------------------------------------------------------------ steam

  function patchSteam(id: number, patch: Partial<SteamSession>) {
    const steam = get().steam;
    if (steam?.id === id) set({ steam: { ...steam, ...patch } });
  }

  /** Every Status: Taretto's steam shows (and when it's ready), or it's over, or a leave asked for before a reconnect goes on. */
  function steamStatus(d: MachineDriver, s: MachineState) {
    const steam = get().steam;
    if (!steam || steam.phase === 'off') return;
    if (inSteam(s)) {
      // The device may notify steam mode before its Reply to enter_steam_mode comes.
      const readyAfterMs = steam.readyAfterMs ?? (s.steam === 'ready' ? Math.round(performance.now() - steam.at) : null);
      if (!steam.seen || readyAfterMs !== steam.readyAfterMs) patchSteam(steam.id, { seen: true, readyAfterMs });
      if (steam.phase === 'leaving' && leavingId !== steam.id && d.connected) void leaveLoop(d, steam.id, null);
    } else if (steam.phase !== 'entering' && (steam.seen || steam.phase === 'leaving' || s.power === 'off')) {
      // Brew heat after steam mode: Taretto's leave, the machine's own button, a cap, or the machine turned off.
      finishSteam(get().steam!);
    }
  }

  function finishSteam(steam: SteamSession) {
    set({ steam: { ...steam, phase: 'off' } });
    if (get().steamNotice === STEAM_STILL_ON) set({ steamNotice: null });
    if (!get().virtual) {
      const ended = steam.phase !== 'leaving' ? 'machine' : steam.leaveLate ? 'by_hand' : 'taretto';
      track('machine_steam', { ready: steamReadyBucket(steam.readyAfterMs), ended });
    }
  }

  /** leave_steam_mode until Status shows brew heat. `via` is reported once, for the leave Taretto was asked for. */
  async function leaveLoop(d: MachineDriver, id: number, via: SteamLeaveVia | null) {
    leavingId = id;
    let first: MachineCommandResult | null = null;
    const over = () => get().steam?.id !== id || get().steam?.phase === 'off';
    try {
      for (let i = 0; i < STEAM_ATTEMPTS; i++) {
        // A drop leaves it asked for: the next Status after a reconnect picks it up again.
        if (driver !== d || !d.connected || over()) return;
        let result: MachineCommandResult;
        try {
          result = await d.send('leaveSteamMode');
        } catch {
          return;
        }
        first ??= result;
        if (await watchFor(d, (s) => !inSteam(s), STEAM_FOLLOW_MS)) return;
      }
      if (driver !== d || over()) return;
      patchSteam(id, { leaveLate: true });
      set({ steamNotice: STEAM_STILL_ON });
    } finally {
      if (leavingId === id) leavingId = null;
      if (via && first) report('leave_steam_mode', first, !inSteam(d.state), via);
    }
  }

  return {
    status: 'disconnected',
    deviceName: null,
    info: null,
    state: null,
    error: null,
    notice: null,
    pending: null,
    pairing: false,
    knownMachine: null,
    virtual: false,
    shot: null,
    starting: false,
    steam: null,
    steamNotice: null,

    async connect({ virtual = false } = {}) {
      retire();
      const gen = generation;
      // Another machine, maybe: steam from the last one isn't this one's to switch back.
      set({ status: 'connecting', deviceName: null, error: null, notice: null, virtual, steam: null, steamNotice: null, ...DISCONNECTED });
      try {
        let d: MachineDriver;
        if (import.meta.env.DEV && virtual) {
          const v = await import('./virtual');
          if (gen !== generation) return;
          const made = v.createVirtualMachine();
          virtualDevice = made.device;
          d = made.driver;
        } else {
          if (typeof navigator === 'undefined' || !navigator.bluetooth) throw new Error('Web Bluetooth isn’t available in this browser.');
          // Synchronous up to requestDevice(): the picker needs the click's user activation. The driver loads meanwhile.
          const picked = requestMachineDevice();
          const mod = loadDriver();
          const device = await picked;
          if (gen !== generation) return;
          d = new (await mod).ManettaMachine(device);
          if (gen !== generation) return;
        }
        await open(d, gen, 'picker');
      } catch (e) {
        if (gen !== generation) return;
        if (isCancel(e)) {
          set({ status: 'disconnected', deviceName: null, error: null });
          return;
        }
        trackFailed('picker', e);
        set({ status: 'error', error: e instanceof Error ? e.message : String(e), ...DISCONNECTED });
      }
    },

    async reconnect() {
      const d = known;
      if (!d) return;
      retire();
      const gen = generation;
      set({ status: 'connecting', error: null, notice: null, ...DISCONNECTED });
      try {
        await open(d, gen, 'reconnect');
      } catch (e) {
        if (gen !== generation) return;
        trackFailed('reconnect', e);
        set({ status: 'error', error: e instanceof Error ? e.message : String(e), ...DISCONNECTED });
      }
    },

    async disconnect() {
      retire();
      writeRemembered(null);
      // Steam mode carries on without the connection (SPEC 5.15), and Taretto can't switch it back any more.
      set({ status: 'disconnected', error: null, notice: null, steam: null, steamNotice: null, ...DISCONNECTED });
    },

    async tryAutoConnect() {
      if (get().status !== 'disconnected') return;
      const id = readRemembered();
      if (!id || typeof navigator === 'undefined' || typeof navigator.bluetooth?.getDevices !== 'function') return;
      const gen = generation;
      let device: BluetoothDevice | undefined;
      try {
        device = (await navigator.bluetooth.getDevices()).find((x) => x.id === id);
      } catch {
        return;
      }
      if (!device || gen !== generation || get().status !== 'disconnected') return;
      set({ status: 'connecting', error: null, virtual: false, ...DISCONNECTED });
      let d: MachineDriver | null = null;
      try {
        d = new (await loadDriver()).ManettaMachine(device);
        if (gen !== generation) return;
        await open(d, gen, 'auto', AUTO_CONNECT_TIMEOUT_MS);
      } catch {
        if (gen !== generation) return;
        // Quietly, and not counted: it may just be unplugged. Reconnect stays one tap away.
        if (d) {
          known = d;
          set({ knownMachine: d.name });
        }
        set({ status: 'disconnected', deviceName: null, error: null, ...DISCONNECTED });
      }
    },

    async setPower(on, via = 'button') {
      const d = driver;
      if (!d || !d.connected || get().pending) return false;
      const command: MachineCommand = on ? 'turnOn' : 'turnOff';
      const usageCommand = on ? 'turn_on' : 'turn_off';
      set({ pending: command, notice: null, pairing: !d.paired });
      try {
        for (let i = 0; i < POWER_ATTEMPTS; i++) {
          const result = await d.send(command);
          if (driver !== d) return false;
          pairingFailures = 0;
          set({ pairing: false });
          if (result !== 'ok') {
            report(usageCommand, result, false, via);
            set({ notice: commandProblem(command, result) });
            return false;
          }
          if (await follows(d, on, POWER_FOLLOW_MS)) {
            report(usageCommand, result, true, via);
            return true;
          }
          if (driver !== d) return false;
        }
        report(usageCommand, 'ok', false, via);
        set({ notice: on ? MACHINE_DIDNT_TURN_ON : MACHINE_DIDNT_TURN_OFF });
        return false;
      } catch (e) {
        if (e instanceof MachinePairingError) {
          report(usageCommand, 'not_paired', false, via);
          set({ notice: pairingNotice(e) });
        } else if (driver === d) {
          set({ notice: e instanceof Error ? e.message : String(e) });
        }
        return false;
      } finally {
        if (driver === d || driver === null) set({ pending: null, pairing: false });
      }
    },

    dismissNotice() {
      set({ notice: null, steamNotice: null });
    },

    async startShot() {
      const d = driver;
      if (!d || !d.connected || get().starting || get().pending || shotRunning(get().shot)) return false;
      set({ starting: true, notice: null, pairing: !d.paired });
      try {
        const { result, brewId } = await d.startBrew(SHOT_MAX_MS);
        if (driver !== d) return false;
        pairingFailures = 0;
        set({ pairing: false });
        if (result !== 'ok' || brewId == null) {
          report('start_brew', result, false, 'button');
          set({ notice: startProblem(result, d.state) });
          return false;
        }
        beginShot(d, 'taretto', d.state?.bootId ?? 0, brewId, true);
        void watchFor(d, (s) => s.phase === 'brewing' && s.brewId === brewId, STOP_CONFIRM_MS).then((followed) => report('start_brew', 'ok', followed, 'button'));
        return true;
      } catch (e) {
        if (e instanceof MachinePairingError) {
          report('start_brew', 'not_paired', false, 'button');
          set({ notice: pairingNotice(e) });
        } else if (driver === d) {
          set({ notice: e instanceof Error ? e.message : String(e) });
        }
        return false;
      } finally {
        if (driver === d || driver === null) set({ starting: false, pairing: false });
      }
    },

    followShot() {
      const d = driver;
      const s = d?.state;
      if (!d || !s || s.phase !== 'brewing') return null;
      const shot = get().shot;
      if (shotRunning(shot) && shot!.bootId === s.bootId && shot!.brewId === s.brewId) return shot;
      // The brew step backdates its timer to this shot's start.
      return beginShot(d, 'machine', s.bootId, s.brewId, true);
    },

    stopShot(via = 'stop') {
      const d = driver;
      let shot = get().shot;
      if (!shotRunning(shot)) {
        // Not followed: started on the machine before the step waited for it. It's still the shot in the cup.
        const s = d?.state;
        if (!d || !s || s.phase !== 'brewing') return;
        shot = beginShot(d, 'machine', s.bootId, s.brewId, false);
      }
      if (shot!.stopAsked) return;
      const stopping: MachineShot = { ...shot!, phase: 'stopping', stopAsked: true };
      set({ shot: stopping });
      if (d?.connected) void stopLoop(d, stopping, via);
    },

    forgetShot() {
      const shot = get().shot;
      if (shotRunning(shot) && !shot!.stopAsked) get().stopShot('leave');
      endShotTimers();
      set({ shot: null });
    },

    async enterSteam(via) {
      const d = driver;
      if (!d || !d.connected || !d.info?.capabilities.steam || inSteam(d.state)) return false;
      const current = get().steam;
      if (current && current.phase !== 'off') return false;
      const steam: SteamSession = { id: ++steamSeq, at: performance.now(), phase: 'entering', seen: false, readyAfterMs: null, leaveLate: false };
      const id = steam.id;
      const mine = () => driver === d && get().steam?.id === id;
      leaveAfterEnter = null;
      set({ steam, steamNotice: null, pairing: !d.paired });
      let first: MachineCommandResult | null = null;
      try {
        for (let i = 0; i < STEAM_ATTEMPTS; i++) {
          const result = await d.enterSteamMode(STEAM_MAX_MS);
          if (!mine()) return false;
          pairingFailures = 0;
          set({ pairing: false });
          if (result !== 'ok') {
            report('enter_steam_mode', result, false, via);
            set({ steam: null, steamNotice: steamProblem(result) });
            return false;
          }
          if (first == null) {
            first = result;
            // Asked to switch back while the command was on its way: it's Taretto's steam now, so back it goes.
            const leave = takeLeaveAfterEnter(id);
            patchSteam(id, { phase: leave ? 'leaving' : 'on', seen: get().steam!.seen || inSteam(d.state) });
            if (leave) {
              report('enter_steam_mode', result, inSteam(d.state), via);
              void leaveLoop(d, id, leave);
              return false;
            }
          }
          if (await watchFor(d, inSteam, STEAM_FOLLOW_MS)) {
            report('enter_steam_mode', result, true, via);
            return true;
          }
          // Switched back meanwhile, or the link went (the device took it, so the session stays for after the
          // reconnect): no second enter_steam_mode.
          if (!mine() || get().steam!.phase !== 'on' || !d.connected) {
            report('enter_steam_mode', result, false, via);
            return false;
          }
        }
        report('enter_steam_mode', 'ok', false, via);
        set({ steam: null, steamNotice: STEAM_DIDNT_START });
        return false;
      } catch (e) {
        if (!mine()) return false;
        if (e instanceof MachinePairingError) {
          report('enter_steam_mode', 'not_paired', false, via);
          set({ steamNotice: pairingNotice(e) });
        } else {
          set({ steamNotice: e instanceof Error ? e.message : String(e) });
        }
        // Taken by the device before the link went? Then Status shows it, and it's left to the person.
        set({ steam: null });
        return false;
      } finally {
        takeLeaveAfterEnter(id);
        if (driver === d || driver === null) set({ pairing: false });
      }
    },

    leaveSteam(via) {
      const steam = get().steam;
      if (!steam || steam.phase === 'off' || steam.phase === 'leaving') return;
      if (steam.phase === 'entering') {
        // On its way: enterSteam switches back once the device has taken it.
        leaveAfterEnter = { id: steam.id, via };
        return;
      }
      set({ steam: { ...steam, phase: 'leaving' } });
      const d = driver;
      if (d?.connected) void leaveLoop(d, steam.id, via);
    },
  };
});
